

import { makeObservable } from 'mobx';
import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import { PageName } from '../../pages/pageSchema';
import { RouteComponentType } from '../../routes/routeSchema';
import type { History } from 'history';
import { RouteContext } from '../../routes/routeContext';

import { INIT_DEBUGGER } from '../../core/debug/debugMacros';
import Routes, { UserDashboardQueryParams, UserDashboardView } from '../../routes';
import { queryParamsToString } from '../../core/urlUtils';
import { ObjectLiteral } from '../../core';

function getRouteParam(params: URLSearchParams | ObjectLiteral | null | undefined, key: string): string | undefined {
  if (!params)
    return undefined;

  if (params instanceof URLSearchParams)
    return params.get(key) ?? undefined;

  const value = params[key];
  return typeof value === 'string' ? value : undefined;
}

function isUserDashboardView(value: string): value is UserDashboardView {
  return value === 'gdrive' || value === 'zoom' || value === 'onedrive';
}

export class BackRouteHandler
  extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable(this);

    INIT_DEBUGGER(this);
  }
  readonly nodeType = 'BackRouteHandler';

  generateBackRoute = (previousContext: RouteContext | null, currentContext: RouteContext, history: History): string | null | (() => void) => {
    if (currentContext.componentType === RouteComponentType.Widget) {
      if (previousContext) {
        return () => history.back();
      }

      return null;
    }

    switch (currentContext.pageName) {
      case PageName.Analytics:
      case PageName.BookmarkList:
      case PageName.CreateTeam:
      case PageName.JoinInvitation:
      case PageName.LiveStreamQueue:
      case PageName.Profile:
      case PageName.SearchResults:
      case PageName.TeamAnalytics:
      case PageName.TeamSettings:
      case PageName.TrainerPlayer:
      case PageName.DocumentUpload:
      case PageName.Upload:
      case PageName.UserSplash:
      case PageName.CliprGptSearchResults: {
        if (previousContext) {
          return () => history.back();
        }

        const params = currentContext.params || currentContext.searchParams;
        const teamId = getRouteParam(params, 'teamId');
        const listId = getRouteParam(params, 'listId');
        const view = getRouteParam(params, 'view');

        if (listId) return Routes.bookmarkList(listId);
        if (teamId) {
          return Routes.teamDashboard(teamId);
        }

        if (teamId && view) {
          const params = { view };
          const search = queryParamsToString(params);

          return `${Routes.teamUpload(teamId)}?${search}`;
        }

        if (view && isUserDashboardView(view)) {
          const viewQueryParam: UserDashboardQueryParams = { view };
          return Routes.userDashboard(viewQueryParam);
        }

        return Routes.userDashboard();
      }

      case PageName.UserPlayer:
      case PageName.DocumentViewer: {
        const params = currentContext.params || currentContext.searchParams;
        const teamId = getRouteParam(params, 'teamId');
        const listId = getRouteParam(params, 'listId');
        if (previousContext && previousContext.pageName === PageName.UserSplash) {
          return Routes.userDashboard();
        }

        if (previousContext) {
          return () => history.back();
        }

        if (listId) return Routes.bookmarkList(listId);
        if (teamId) {
          return Routes.teamDashboard(teamId);
        }
        return Routes.userDashboard();
      }

      case PageName.TrainerDocumentViewer: {
        if (previousContext) {
          return () => history.back();
        }

        const teamId = getRouteParam(currentContext.params || currentContext.searchParams, 'teamId');
        if (teamId) {
          return Routes.teamDashboard(teamId);
        }
        return Routes.trainerDashboard();
      }

      default:
        return null;
    }
  }
}
